import { SegmentedControl, XStack, YStack } from '@shinetools/lumen-react'
import { useTranslation } from 'react-i18next'

import { Contact } from '@features/contacts/types/contact'
import { ContactType } from '@features/contacts/types/contactType'

import { DesignSystem } from '../../../../../../../../../../enums/designSystem'
import { useFormContext, useWatch } from '../../../../../../../../../../hooks/useForm'
import type { WithTrackingContext } from '../../../../../../../../../../types/withTrackingContext'
import type { SupplierFormSchema, SupplierFormValues } from '../../types/supplierForm'
import { getSupplierFormDefaultValues } from '../../utils/supplierForm'
import { ManageCompany } from './elements/ManageCompany'
import { ManageIndividual } from './elements/ManageIndividual'

interface ManageSupplierProps extends WithTrackingContext {
  contact: Partial<Contact> | null
}

const isContactType = (value: string): value is ContactType =>
  value === ContactType.Company || value === ContactType.Person

// A company registry pick identifies one specific company record. That identity stops applying
// once the supplier being created turns out to be a person, so these two get cleared on switch;
// everything else typed so far (name, address, phone, additional contacts...) still carries over.
const COMPANY_IDENTITY_FIELDS = ['registrationNo', 'vatNo'] as const

export const ManageSupplier = ({ contact, trackingContext }: ManageSupplierProps) => {
  const { t } = useTranslation()
  const { FormItem, getValues, reset } = useFormContext<SupplierFormSchema, DesignSystem.Lumen>({
    designSystem: DesignSystem.Lumen,
  })
  const type = useWatch<SupplierFormValues, 'type'>({ name: 'type' })

  const handleTypeChange = (value: string) => {
    if (!isContactType(value)) {
      return
    }

    // A saved supplier has an id: switching tabs there only reveals the other set of fields,
    // filled from that saved supplier — it is not an edit in itself. A supplier being created has
    // no id yet, so there is no saved snapshot to reveal; resetting from it would instead drop
    // whatever the user already typed.
    if (contact?.id) {
      reset(getSupplierFormDefaultValues(contact, value))
      return
    }

    const currentValues = getValues()
    const clearedCompanyIdentityFields = Object.fromEntries(COMPANY_IDENTITY_FIELDS.map((field) => [field, '']))

    reset({ ...currentValues, ...clearedCompanyIdentityFields, type: value })
  }

  return (
    <YStack gap={16}>
      {/* The stack would stretch the control, so the row keeps it at its natural width. */}
      <XStack>
        <FormItem
          name="type"
          render={({ field }) => (
            <SegmentedControl
              aria-label={t('view')}
              items={[
                {
                  label: t('suppliers.detail.form.type.company'),
                  value: ContactType.Company,
                },
                {
                  label: t('suppliers.detail.form.type.individual'),
                  value: ContactType.Person,
                },
              ]}
              onValueChange={handleTypeChange}
              size="medium"
              value={field.value}
            />
          )}
        />
      </XStack>
      {type === ContactType.Person ? (
        <ManageIndividual trackingContext={trackingContext} />
      ) : (
        <ManageCompany trackingContext={trackingContext} />
      )}
    </YStack>
  )
}
